import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, COMMUNITY_PHOTOS } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Award, RefreshCw, Quote } from 'lucide-react';

const SpotlightsPage = () => {
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpotlights = async () => {
      try {
        const response = await axios.get(`${API}/spotlights`);
        setSpotlights(response.data);
      } catch (error) {
        console.error('Failed to fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpotlights();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="relative h-64 md:h-80">
        <img src={COMMUNITY_PHOTOS.family} alt="Killarney Community" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-plp-blue/90 to-plp-blue/70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Award className="w-16 h-16 text-plp-gold mx-auto mb-4" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Resident Spotlights
            </h1>
            <p className="mt-4 text-white/80 max-w-2xl mx-auto px-4">
              Celebrating the achievements and contributions of Killarney community members.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
          </div>
        ) : spotlights.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No spotlights yet. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spotlights.map((person) => (
              <Card key={person.id} className="bg-white overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="relative">
                    {person.image_url ? (
                      <img 
                        src={person.image_url} 
                        alt={person.name} 
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 bg-gradient-to-br from-plp-gold/20 to-plp-blue/20 flex items-center justify-center">
                        <Award className="w-20 h-20 text-plp-gold/50" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                      <h3 className="font-heading font-bold text-white text-xl">{person.name}</h3>
                      {person.title && <p className="text-white/80 text-sm">{person.title}</p>}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-2 mb-4">
                      <div className="w-1 h-full bg-plp-gold rounded-full"></div>
                      <div>
                        <p className="text-xs text-plp-blue font-semibold uppercase tracking-wide">Achievement</p>
                        <p className="text-emerald-600 font-medium">{person.achievement}</p>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{person.bio}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <Card className="mt-12 bg-plp-gold/10 border-plp-gold/30">
          <CardContent className="p-8 text-center">
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
              Know Someone Making a Difference?
            </h3>
            <p className="text-slate-600 mb-4">
              Nominate a Killarney resident for our Community Spotlight! 
              Contact the campaign team to share their story.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SpotlightsPage;
